'use client';

import React, { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { PersonioApiOpenPosition } from '@/types/career-types';
import { slugify } from '@/utils/url-helper';
import { buildTrackingId } from '@/utils/tracking-helper';
import { useLocalization } from '@/ui/providers/localizationProvider';
import { RichTextFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import Button from '../../shared/Button';
import { RichTextRenderer } from '../../tileRenderers/RichTextRenderer';

export type JobListProps = {
  tile: JobListTile;
  fetchJobs: (language: string) => Promise<PersonioApiOpenPosition[]>;
  trackingPrefix?: string;
};

export type JobListTile = {
  heading?: RichTextFragment;
};

type JobDescriptionItem = {
  name: string;
  value: string;
};

export default function JobList({ tile, fetchJobs, trackingPrefix }: Readonly<JobListProps>) {
  const [jobs, setJobs] = useState<PersonioApiOpenPosition[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const t = useTranslations('jobListTile');
  const { language } = useLocalization();
  let jobDescriptionIdentifier: string = 'Job Description';
  let languageForFetchingJobs: string = 'en';
  if (language === 'de') {
    jobDescriptionIdentifier = 'Stellenbeschreibung';
    languageForFetchingJobs = 'de';
  }
  useEffect(() => {
    const fetchJobsData = async () => {
      setHasError(false);
      try {
        setIsLoading(true);
        const fetchedJobs = await fetchJobs(languageForFetchingJobs);
        setJobs(fetchedJobs);
      } catch (error) {
        console.error('Failed to fetch jobs:', error);
        setHasError(true);
        setJobs([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchJobsData();
  }, []);

  const getJobUrl = (job: PersonioApiOpenPosition) =>
    `/company/careers/open-positions/${job.id}-${slugify(job.name)}`;

  // Helper function to render loading state
  const renderLoadingState = () => (
    <div className="flex h-40 items-center justify-center">
      <div className="h-12 w-12 animate-spin rounded-full border-4 border-neutral-20 border-t-primary-100" />
    </div>
  );

  // Helper function to render error state
  const renderErrorState = () => (
    <div className="rounded-xl bg-neutral-5 p-8 text-center">
      <p className="text-lg">
        {t('Could not load job offerings at the moment. Please check again later.')}
      </p>
    </div>
  );

  // Helper function to render "no openings" state
  // TODO: adapt href for unsolicited application once the page is available
  const renderEmptyState = () => (
    <div className="rounded-xl bg-neutral-5 p-8 text-center">
      <h1 className="p-4 text-3xl font-medium">{t('NoPositionsHeader')}</h1>
      <p className="mx-auto max-w-3xl text-md">{t('NoPositionsText')}</p>
      <Button
        className="mt-6 inline-block"
        href={`mailto:careers@nuki.io?subject=${t('UnsolicitedApplicationMailtoSubject')}&body=${t('UnsolicitedApplicationMailtoBody')}`}
        data-tile-element-id={buildTrackingId(trackingPrefix, 'unsolicited-application')}
      >
        {t.raw('UnsolicitedApplicationButton')}
      </Button>
    </div>
  );

  const getAndCleanUpJobDescription = (
    descriptions: JobDescriptionItem[],
    keyName: string,
  ): string => {
    const description = descriptions.find((desc) => desc.name === keyName);

    if (description) {
      let cleaned = description.value.replace(/.*<br\s\/>/, '');
      const doc = new DOMParser().parseFromString(cleaned, 'text/html');
      cleaned = doc.documentElement.textContent as string;
      const maxLength = 300;
      if (cleaned.length <= maxLength) {
        return cleaned;
      }
      let truncated = cleaned.substring(0, maxLength);
      const lastPeriodIndex = truncated.lastIndexOf('.');
      if (lastPeriodIndex !== -1) {
        truncated = `${truncated.substring(0, lastPeriodIndex + 1)}`;
      } else {
        truncated = `${truncated}...`;
      }
      return truncated;
    }
    return '';
  };

  // Helper function to render job list
  const renderJobList = () => (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      {jobs?.map((job) => {
        const roleDescription = getAndCleanUpJobDescription(
          job.jobDescriptions.jobDescription,
          jobDescriptionIdentifier,
        );
        const jobButtonTrackingId = buildTrackingId(trackingPrefix, job.name);

        return (
          <div
            key={job.id}
            className="job-card bg-gra bg- flex h-full flex-col justify-between rounded-xl bg-neutral-5 p-6 shadow-lg"
          >
            <div>
              <h3 className="mb-2 text-xl font-bold">{job.name}</h3>
            </div>
            <div className="flex flex-wrap justify-end">
              <p>{roleDescription}</p>
            </div>
            <div className="flexs mt-4 justify-start">
              <Button
                href={getJobUrl(job)}
                className="w-2/5"
                variant="primary"
                data-tile-element-id={jobButtonTrackingId}
              >
                {t('Find out more')}
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );

  // Determine which content to render
  let content;
  if (isLoading) {
    content = renderLoadingState();
  } else if (hasError) {
    content = renderErrorState();
  } else if (jobs.length === 0) {
    content = renderEmptyState();
  } else {
    content = renderJobList();
  }

  return (
    <div className="mx-auto max-w-screen-2xl overflow-x-hidden px-4 sm:px-8">
      <div className="job-list-tile py-8">
        {tile.heading && (
          <RichTextRenderer
            blocks={tile.heading}
            className="mb-12 bg-gray-500 text-4xl font-bold"
          />
        )}
        {content}
      </div>
    </div>
  );
}
